/*=============================================== PaginationPage ===============================================*/

import { Link } from "react-router-dom"
import { Text } from "tsx-library-julseb"

import { ComponentsLayout } from "components"
import type { ItemType, PropsItemType } from "components"

import { commonProps, iconBoxicon } from "data"

const PaginationPage = () => {
    const items: ItemType[] = [
        {
            title: "Pagination",
            intro: commonProps.routerOnly,
            content: (
                <Text>
                    <Link
                        to="/components/pagination/demo"
                        target="_blank"
                        rel="noreferrer noopener"
                    >
                        Demo here.
                    </Link>
                </Text>
            ),
            // eslint-disable-next-line
            code: 'const arr = generateNumbers(1, 201).map(n => `Hello ${n}`)\nconst { getNumberOfPages, getPaginatedData } = usePaginatedData(arr)\n\n{getPaginatedData() && (\n    <Grid col={4} gap="s">\n        {getPaginatedData().map(text => (\n            <Card border={{ width: 1 }} key={uuid()}>\n                <Text>{text}</Text>\n            </Card>\n        ))}\n    </Grid>\n)}\n\n{getNumberOfPages() > 1 && (\n    <Pagination totalPages={getNumberOfPages()} icons={{ prev: "arrow-left", next: "arrow-right" }} />\n)}',
            // eslint-disable-next-line
            codeTs: 'const arr: string[] = generateNumbers(1, 201).map(n => `Hello ${n}`)\nconst { getNumberOfPages, getPaginatedData } = usePaginatedData(arr)\n\n{getPaginatedData() && (\n    <Grid col={4} gap="s">\n        {getPaginatedData().map(text => (\n            <Card border={{ width: 1 }} key={uuid()}>\n                <Text>{text}</Text>\n            </Card>\n        ))}\n    </Grid>\n)}\n\n{getNumberOfPages() > 1 && (\n    <Pagination totalPages={getNumberOfPages()} icons={{ prev: "arrow-left", next: "arrow-right" }} />\n)}',
        },
        {
            title: "Pagination with query",
            intro: commonProps.routerOnly,
            content: (
                <Text>
                    <Link
                        to="/components/pagination/demo-query"
                        target="_blank"
                        rel="noreferrer noopener"
                    >
                        Demo here.
                    </Link>
                </Text>
            ),
            // eslint-disable-next-line
            code: 'const arr = generateNumbers(1, 201).map((n) => `Hello ${n}`)\nconst { getNumberOfPages, getPaginatedData } = usePaginatedData(arr)\nconst searchParams = [\n    ["search", "hello"],\n    ["order", "asc"],\n]\n\n{getPaginatedData() && (\n    <Grid col={4} gap="s">\n        {getPaginatedData().map(text => (\n            <Card border={{ width: 1 }} key={uuid()}>\n                <Text>{text}</Text>\n            </Card>\n        ))}\n    </Grid>\n)}\n\n{getNumberOfPages() > 1 && (\n    <Pagination totalPages={getNumberOfPages()} queries={searchParams} />\n)}',
            // eslint-disable-next-line
            codeTs: 'const arr: string[] = generateNumbers(1, 201).map((n) => `Hello ${n}`)\nconst { getNumberOfPages, getPaginatedData } = usePaginatedData(arr)\nconst searchParams: string[][] = [\n    ["search", "hello"],\n    ["order", "asc"],\n]\n\n{getPaginatedData() && (\n    <Grid col={4} gap="s">\n        {getPaginatedData().map(text => (\n            <Card border={{ width: 1 }} key={uuid()}>\n                <Text>{text}</Text>\n            </Card>\n        ))}\n    </Grid>\n)}\n\n{getNumberOfPages() > 1 && (\n    <Pagination totalPages={getNumberOfPages()} queries={searchParams} />\n)}',
        },
        {
            title: "Custom navigation",
            content: (
                <Text>
                    <Link
                        to="/components/pagination/demo-custom"
                        target="_blank"
                        rel="noreferrer noopener"
                    >
                        Demo here.
                    </Link>
                </Text>
            ),
            // eslint-disable-next-line
            code: 'const arr = generateNumbers(1, 201).map((n) => `Hello ${n}`)\n\nconst pageLimit = 5\nconst dataLimit = 20\n\nconst [searchParams, setSearchParams] = useSearchParams()\nconst page = searchParams.get("page") || "1"\n\nconst [currentPage, setCurrentPage] = useState(parseInt(page))\n\nconst handlePrev = () => {\n    setSearchParams({ page: (currentPage - 1).toString() })\n    setCurrentPage(currentPage - 1)\n    scrollToTop()\n}\n\nconst handleNext = () => {\n    setSearchParams({ page: (currentPage + 1).toString() })\n    setCurrentPage(currentPage + 1)\n    scrollToTop()\n}\n\nconst handlePage = i => {\n    setSearchParams({ page: i.toString() })\n    setCurrentPage(i)\n    scrollToTop()\n}\n\nconst totalPages = Math.ceil((arr?.length || 0) / dataLimit)\n\nconst getPaginationGroup = () => {\n    let start = Math.floor((currentPage - 1) / pageLimit) * pageLimit\n    return (\n        new Array(pageLimit)\n            .fill()\n            .map((_, i) => start + i + 1)\n            .filter(item => item <= totalPages)\n    )\n}\n\nconst getPaginatedData = () => {\n    const startIndex = currentPage * dataLimit - dataLimit\n    const endIndex = startIndex + dataLimit\n    return arr?.slice(startIndex, endIndex)\n}\n\n{getPaginatedData() && (\n    <Grid col={4} gap="s">\n        {getPaginatedData().map(text => (\n            <Card border={{ width: 1 }} key={uuid()}>\n                <Text>{text}</Text>\n            </Card>\n        ))}\n    </Grid>\n)}\n\n{totalPages > 1 && (\n    <PaginationContainer>\n        <PaginationButton\n            onClick={handlePrev}\n            buttonContent="prev"\n            disabled={currentPage === 1}\n        />\n\n        {getPaginationGroup()[0] !== 1 && (\n            <>\n                <PaginationButton\n                    buttonContent={1}\n                    onClick={() => handlePage(1)}\n                />\n                <PaginationButton buttonContent="more" />\n            </>\n        )}\n\n        {getPaginationGroup().map(item => (\n            <PaginationButton\n                buttonContent={item}\n                key={item}\n                onClick={() => handlePage(item)}\n                isActive={currentPage === item}\n            />\n        ))}\n\n        {getPaginationGroup()[getPaginationGroup().length - 1] !==\n            totalPages && (\n            <>\n                <PaginationButton buttonContent="more" />\n\n                <PaginationButton\n                    buttonContent={totalPages}\n                    onClick={() => handlePage(totalPages)}\n                />\n            </>\n        )}\n\n        <PaginationButton\n            onClick={handleNext}\n            buttonContent="next"\n            disabled={currentPage === totalPages}\n        />\n    </PaginationContainer>\n)}',
            // eslint-disable-next-line
            codeTs: 'const arr: string[] = generateNumbers(1, 201).map((n) => `Hello ${n}`)\n\nconst pageLimit: number = 5\nconst dataLimit: number = 20\n\nconst [searchParams, setSearchParams] = useSearchParams()\nconst page: string = searchParams.get("page") || "1"\n\nconst [currentPage, setCurrentPage] = useState<number>(parseInt(page))\n\nconst handlePrev = () => {\n    setSearchParams({ page: (currentPage - 1).toString() })\n    setCurrentPage(currentPage - 1)\n    scrollToTop()\n}\n\nconst handleNext = () => {\n    setSearchParams({ page: (currentPage + 1).toString() })\n    setCurrentPage(currentPage + 1)\n    scrollToTop()\n}\n\nconst handlePage = (i: number) => {\n    setSearchParams({ page: i.toString() })\n    setCurrentPage(i)\n    scrollToTop()\n}\n\nconst totalPages: number = Math.ceil((arr?.length || 0) / dataLimit)\n\nconst getPaginationGroup = () => {\n    let start: number = Math.floor((currentPage - 1) / pageLimit) * pageLimit\n    return (\n        new Array(pageLimit)\n            .fill()\n            .map((_, i) => start + i + 1)\n            .filter((item) => item <= totalPages)\n    )\n}\n\nconst getPaginatedData = () => {\n    const startIndex: number = currentPage * dataLimit - dataLimit\n    const endIndex: number = startIndex + dataLimit\n    return arr?.slice(startIndex, endIndex)\n}\n\n{getPaginatedData() && (\n    <Grid col={4} gap="s">\n        {getPaginatedData().map(text => (\n            <Card border={{ width: 1 }} key={uuid()}>\n                <Text>{text}</Text>\n            </Card>\n        ))}\n    </Grid>\n)}\n\n{totalPages > 1 && (\n    <PaginationContainer>\n        <PaginationButton\n            onClick={handlePrev}\n            buttonContent="prev"\n            disabled={currentPage === 1}\n        />\n\n        {getPaginationGroup()[0] !== 1 && (\n            <>\n                <PaginationButton\n                    buttonContent={1}\n                    onClick={() => handlePage(1)}\n                />\n                <PaginationButton buttonContent="more" />\n            </>\n        )}\n\n        {getPaginationGroup().map(item => (\n            <PaginationButton\n                buttonContent={item}\n                key={item}\n                onClick={() => handlePage(item)}\n                isActive={currentPage === item}\n            />\n        ))}\n\n        {getPaginationGroup()[getPaginationGroup().length - 1] !==\n            totalPages && (\n            <>\n                <PaginationButton buttonContent="more" />\n\n                <PaginationButton\n                    buttonContent={totalPages}\n                    onClick={() => handlePage(totalPages)}\n                />\n            </>\n        )}\n\n        <PaginationButton\n            onClick={handleNext}\n            buttonContent="next"\n            disabled={currentPage === totalPages}\n        />\n    </PaginationContainer>\n)}',
        },
    ]

    const propsItems: PropsItemType[] = [
        {
            title: (
                <>
                    <code>Pagination</code> component
                </>
            ),
            subtitle: "as",
            intro: "Changes the HTML tag",
            type: "string",
            possibleString: "Any HTML tag",
            default: "div",
        },
        {
            subtitle: "totalPages",
            type: "number",
            required: true,
        },
        {
            subtitle: "queries",
            type: "array of array of strings",
        },
        {
            subtitle: "pageLimit",
            intro: "Numbers of pages shown.",
            type: "number",
            default: 5,
        },
        "separator-small",
        {
            subtitle: "icons",
            type: "object",
        },
        {
            title: (
                <>
                    <code>icons</code> object
                </>
            ),
            subtitle: "prev",
            intro: commonProps.addIconCapital,
            type: "string",
            default: iconBoxicon("chevron-left"),
        },
        {
            subtitle: "next",
            type: "string",
            default: iconBoxicon("chevron-right"),
        },
        {
            subtitle: "prevSize",
            type: "number",
            default: 16,
        },
        {
            subtitle: "nextSize",
            type: "number",
            default: 16,
        },
        "separator",
        {
            title: (
                <>
                    <code>PaginationContainer</code> component
                </>
            ),
            subtitle: "as",
            intro: "Changes the HTML tag",
            type: "string",
            possibleString: "Any HTML tag",
            default: "div",
        },
        "separator",
        {
            title: (
                <>
                    <code>PaginationButton</code> component
                </>
            ),
            subtitle: "as",
            intro: "Changes the HTML tag",
            type: "string",
            possibleString: "Any HTML tag",
            default: "button",
        },
        {
            subtitle: "buttonContent",
            type: "number or string",
            possible: ["prev", "next", "more", "any number"],
            required: true,
        },
        {
            subtitle: "isActive",
            intro: (
                <>
                    Only if <code>buttonContent</code> is a <code>number</code>.
                </>
            ),
            type: "boolean",
        },
        {
            subtitle: "icon",
            intro: (
                <>
                    Only if <code>buttonContent</code> is set to{" "}
                    <code>prev</code> or <code>next</code>.
                </>
            ),
            type: "string",
            default: (
                <>
                    Same as the <code>icons</code> prop in the{" "}
                    <code>Pagination</code> component.
                </>
            ),
        },
        {
            subtitle: "iconSize",
            intro: (
                <>
                    Only if <code>buttonContent</code> is set to{" "}
                    <code>prev</code> or <code>next</code>.
                </>
            ),
            type: "number",
            default: (
                <>
                    Same as the <code>icons</code> prop in the{" "}
                    <code>Pagination</code> component.
                </>
            ),
        },
    ]

    return <ComponentsLayout items={items} propsItems={propsItems} />
}

export default PaginationPage
